import { addMinutes } from "date-fns";
import { CalendarDays, Eye, Link2, Pencil, SquarePen } from "lucide-react";
import { useEffect, useState } from "react";

import type { SessionSelect } from "~/../drizzle/schema";
import type { badgeVariants } from "~/components/ui/badge";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/cn";
import { SessionStatus } from "~/lib/schemas/db-enums";
import { sessionDisplayTitle } from "~/lib/shared/session-title";
import {
  formatMonthDay,
  formatTimeOnly,
  getClientTimezoneAbbr,
} from "~/lib/utils/timezone";

type BadgeVariant = NonNullable<Parameters<typeof badgeVariants>[0]>["variant"];

export type SessionRowVariant = "upcoming" | "past";

/**
 * Derive the status pill shown on a session row. Status is automatic: upcoming
 * sessions are always "Upcoming"; past sessions reflect their stored status, and
 * a past session still marked "scheduled" surfaces as "Needs follow-up" so it
 * doesn't silently disappear.
 */
export function getSessionStatusBadge(args: {
  variant: SessionRowVariant;
  status: SessionSelect["status"];
}): { label: string; variant: BadgeVariant; className?: string } {
  if (args.variant === "upcoming") {
    return {
      label: "Upcoming",
      variant: "outline",
      className: "border-purple-200 bg-purple-50 text-purple-700",
    };
  }

  switch (args.status) {
    case SessionStatus.COMPLETED:
      return { label: "Completed", variant: "success" };
    case SessionStatus.NO_SHOW:
      return { label: "No-show", variant: "cancelled" };
    default:
      return {
        label: "Needs follow-up",
        variant: "outline",
        className: "bg-amber-50 text-amber-800 border-amber-200",
      };
  }
}

export interface CoachSessionRowProps {
  variant: SessionRowVariant;
  sessionNumber?: number;
  title: string | null;
  startAt: string | Date;
  duration: number;
  status: SessionSelect["status"];
  goalDescription?: string | null;
  hasMeetingUrl?: boolean;
  /** The session's current meeting link, for the inline link editor. */
  meetingUrl?: string | null;
  /**
   * Persist a new/cleared meeting link inline (no drawer round-trip). When
   * provided on an upcoming row, the row shows an add/edit link affordance. Left
   * undefined by inert surfaces (e.g. the marketing showcase), which then render
   * no link editor.
   */
  onSaveMeetingUrl?: (url: string | null) => void;
  notes?: { coach: number; client: number };
  homeworkCount?: number;
  onEdit: () => void;
  onJoin?: () => void;
  onViewNotes?: () => void;
}

/** Accepts only well-formed http(s) URLs; used to gate the inline link save. */
function isValidHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Inline meeting-link editor: a compact "Add link" / edit affordance that opens a
 * small popover with a URL field. Calm by design — it never dominates the row.
 */
function MeetingLinkControl({
  meetingUrl,
  onSave,
}: {
  meetingUrl?: string | null;
  onSave: (url: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(meetingUrl ?? "");
  const [error, setError] = useState<string | null>(null);

  // Re-seed the field from props whenever the popover (re)opens.
  useEffect(() => {
    if (open) {
      setValue(meetingUrl ?? "");
      setError(null);
    }
  }, [open, meetingUrl]);

  const handleSave = () => {
    const trimmed = value.trim();
    if (trimmed && !isValidHttpUrl(trimmed)) {
      setError("Enter a valid link, e.g. https://zoom.us/j/123");
      return;
    }
    onSave(trimmed || null);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {meetingUrl ? (
          <Button
            size="sm"
            variant="ghost"
            className="gap-2 text-gray-500"
            aria-label="Edit meeting link"
          >
            <Link2 className="h-4 w-4" />
            <Pencil className="h-3 w-3" />
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="gap-2 text-gray-500"
            aria-label="Add meeting link"
          >
            <Link2 className="h-4 w-4" />
            Add link
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-2">
        <Label className="text-xs font-medium">Meeting link</Label>
        <Input
          type="url"
          inputMode="url"
          autoFocus
          placeholder="https://zoom.us/j/…"
          value={value}
          onValueChange={(v) => {
            setValue(v);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSave();
            }
          }}
        />
        {error && <p className="text-xs text-red-700">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" onClick={handleSave}>
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export const CoachSessionRow = ({
  variant,
  sessionNumber,
  title,
  startAt,
  duration,
  status,
  goalDescription,
  hasMeetingUrl,
  meetingUrl,
  onSaveMeetingUrl,
  notes,
  homeworkCount,
  onEdit,
  onJoin,
  onViewNotes,
}: CoachSessionRowProps) => {
  const isUpcoming = variant === "upcoming";
  const start = new Date(startAt);
  const end = addMinutes(start, duration);
  const zoneAbbr = getClientTimezoneAbbr(start);
  const timeRange = `${formatTimeOnly(start)} - ${formatTimeOnly(end)}${
    zoneAbbr ? ` ${zoneAbbr}` : ""
  }`;

  const badge = getSessionStatusBadge({ variant, status });

  const noteCount = (notes?.coach ?? 0) + (notes?.client ?? 0);
  const hasNotes = noteCount > 0;
  // Keep the "#N — Title" form when the coach set a custom title; otherwise fall
  // back to the shared computed default ("Session N"). Never double-prefixes.
  const heading = title
    ? sessionNumber
      ? `Session #${sessionNumber} — ${title}`
      : title
    : sessionDisplayTitle({ title: null, sessionNumber });

  return (
    <Card className="p-4">
      <CardContent>
        {/*
         * Horizontal by default (the real coach timeline). Inside a query
         * container narrower than @2xl (e.g. the marketing showcase in a narrow
         * slot) the row stacks: the date + title identity sits on top, the
         * status pill and actions drop beneath it. In a phone-width slot the date
         * also sits above the title, and the title wraps, so it stays readable.
         * The @max-* variants are inert in the real app, which has no @container
         * ancestor.
         */}
        <div className="flex items-center justify-between gap-4 @max-2xl:flex-col @max-2xl:items-stretch @max-2xl:gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-4 @max-sm:flex-col @max-sm:items-start @max-sm:gap-3">
            <div
              className={cn(
                "flex items-start gap-3 rounded-xl p-4",
                isUpcoming ? "bg-purple-50" : "bg-gray-50",
              )}
            >
              <CalendarDays
                className={cn(
                  "mt-0.5 h-5 w-5",
                  isUpcoming ? "text-purple-700" : "text-text-tertiary",
                )}
              />
              <div className="flex flex-col items-start">
                {/*
                 * These helpers resolve the *viewer's* zone + locale, so the
                 * server (UTC / en-US on Workers) and a visitor's hydration
                 * legitimately disagree — "Aug 20" vs "20 août" — which React
                 * reports as #418 (DCM-SZ). Suppressing keeps a real time in
                 * the server HTML for crawlers instead of deferring the render.
                 *
                 * React then adopts the server's text rather than patching it,
                 * so an SSR'd row keeps the server's zone. That is fine for the
                 * only surface that server-renders rows — the homepage's
                 * fictional showcase timeline. The real coach timeline loads
                 * through react-query on the client (SSR shows skeletons), so a
                 * coach always reads their own timezone. A future surface that
                 * SSRs real session data needs a mounted-flag swap, not this.
                 */}
                <div
                  suppressHydrationWarning
                  className={cn(
                    "text-base font-medium",
                    isUpcoming ? "text-gray-900" : "text-text-secondary",
                  )}
                >
                  {formatMonthDay(start)}
                </div>
                <div
                  suppressHydrationWarning
                  className="text-text-tertiary text-sm font-normal"
                >
                  {formatTimeOnly(start)}
                </div>
              </div>
            </div>

            <div className="min-w-0 flex-1 @max-sm:w-full">
              <div className="truncate text-base font-medium text-gray-900 @max-sm:whitespace-normal">
                {heading}
              </div>
              {/* Carries `timeRange` in two of its branches — same
                  zone-dependent rendering as the date block above. */}
              <div
                suppressHydrationWarning
                className="text-text-secondary text-sm"
              >
                {isUpcoming
                  ? `${timeRange} · Goal: ${goalDescription || "No goal set"}`
                  : hasNotes || (homeworkCount ?? 0) > 0
                    ? [
                        hasNotes
                          ? `${noteCount} note${noteCount === 1 ? "" : "s"}`
                          : null,
                        (homeworkCount ?? 0) > 0
                          ? `${homeworkCount} task${homeworkCount === 1 ? "" : "s"}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : status === SessionStatus.SCHEDULED
                      ? "This session was never marked complete"
                      : timeRange}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 @max-2xl:flex-wrap">
            <Badge
              variant={badge.variant}
              size="sm"
              className={badge.className}
            >
              {badge.label}
            </Badge>

            {!isUpcoming && onViewNotes && (
              <Button
                size="sm"
                className="gap-2 border-secondary text-gray-600"
                onClick={onViewNotes}
                disabled={!hasNotes}
                aria-label="View notes"
              >
                <Eye className="h-4 w-4" />
                Notes
              </Button>
            )}

            {isUpcoming && hasMeetingUrl && onJoin && (
              <Button
                size="sm"
                className="border-secondary text-gray-600"
                onClick={onJoin}
              >
                Join
              </Button>
            )}

            {isUpcoming && onSaveMeetingUrl && (
              <MeetingLinkControl
                meetingUrl={meetingUrl}
                onSave={onSaveMeetingUrl}
              />
            )}

            <Button
              size="sm"
              className="border-secondary text-gray-600"
              onClick={onEdit}
              aria-label="Edit session"
            >
              <SquarePen className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
